/**
 * M1-6: Error overlay displayed when a scene fails to load.
 * Handles three distinct scenarios via the `kind` prop.
 */
export type ErrorKind = 'network' | 'token-invalid' | 'scene-offline' | 'unknown';

interface ErrorOverlayProps {
  kind: ErrorKind;
  message?: string;
  onRetry?: () => void;
}

const ERROR_COPY: Record<ErrorKind, { title: string; description: string }> = {
  network: {
    title: 'Network error',
    description: 'Could not reach the server. Check your connection and try again.',
  },
  'token-invalid': {
    title: 'Preview link expired',
    description: 'This preview link is no longer valid. Please request a new one.',
  },
  'scene-offline': {
    title: 'Scene unavailable',
    description: 'This scene has been taken offline by its creator.',
  },
  unknown: {
    title: 'Something went wrong',
    description: 'An unexpected error occurred while loading the scene.',
  },
};

export function ErrorOverlay({ kind, message, onRetry }: ErrorOverlayProps) {
  const copy = ERROR_COPY[kind];

  return (
    <div className="pointer-events-auto absolute inset-0 z-50 flex flex-col items-center justify-center bg-black/80 px-6">
      <div className="flex w-full max-w-sm flex-col items-center rounded-2xl bg-white/10 p-8 text-center backdrop-blur-md">
        {/* Icon */}
        <div className="mb-4 flex size-14 items-center justify-center rounded-full bg-white/10 text-3xl">
          {kind === 'scene-offline' ? '🚫' : kind === 'token-invalid' ? '🔒' : '⚠️'}
        </div>

        <h2 className="mb-2 text-xl font-semibold text-white">{copy.title}</h2>
        <p className="mb-1 text-sm text-white/60">{copy.description}</p>
        {message && <p className="mt-2 font-mono text-xs break-all text-white/30">{message}</p>}

        {onRetry && kind !== 'scene-offline' && kind !== 'token-invalid' && (
          <button
            type="button"
            onClick={onRetry}
            className="mt-6 rounded-full bg-white px-6 py-2 text-sm font-medium text-black transition-opacity hover:opacity-80"
          >
            Retry
          </button>
        )}
      </div>
    </div>
  );
}
